import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["startDate", "startTime", "endDate", "endTime", "addonCheckbox", "serviceSelect"]
  static values = { baseDuration: Number }

  connect() {
    this.syncBaseDuration()
    this.recalculate()

    // Bind service change directly as a safety net — some browsers/contexts
    // may not reliably fire the declarative data-action on <select> with <optgroup>
    if (this.hasServiceSelectTarget) {
      this.serviceSelectTarget.addEventListener("change", this.handleServiceChange)
    }
  }

  disconnect() {
    if (this.hasServiceSelectTarget) {
      this.serviceSelectTarget.removeEventListener("change", this.handleServiceChange)
    }
  }

  handleServiceChange = () => {
    this.syncBaseDuration()
    this.recalculate()
  }

  toggle() {
    this.recalculate()
  }

  startChanged() {
    this.recalculate()
  }

  serviceChanged() {
    this.syncBaseDuration()
    this.recalculate()
  }

  syncBaseDuration() {
    if (!this.hasServiceSelectTarget) return
    const selected = this.serviceSelectTarget.selectedOptions[0]
    if (selected && selected.dataset.duration) {
      this.baseDurationValue = parseInt(selected.dataset.duration, 10) || 0
    }
  }

  recalculate() {
    if (!this.hasStartDateTarget || !this.hasStartTimeTarget) return
    if (!this.hasEndDateTarget || !this.hasEndTimeTarget) return

    const dateVal = this.startDateTarget.value
    const timeVal = this.startTimeTarget.value
    if (!dateVal || !timeVal) return

    const totalMinutes = this.calculateTotalDuration()
    if (totalMinutes <= 0) return

    const startDate = new Date(`${dateVal}T${timeVal}:00`)
    if (isNaN(startDate.getTime())) return

    const endDate = new Date(startDate.getTime() + totalMinutes * 60 * 1000)

    const pad = (n) => String(n).padStart(2, "0")
    const endDateStr = `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}`
    this.endDateTarget.value = endDateStr

    const rawMinutes = endDate.getMinutes()
    const roundedMinutes = Math.ceil(rawMinutes / 15) * 15
    let snapHour = endDate.getHours()
    let snapMin = roundedMinutes
    if (snapMin === 60) { snapHour++; snapMin = 0 }
    const snappedTime = `${pad(snapHour)}:${pad(snapMin)}`
    const exactTime = `${pad(endDate.getHours())}:${pad(endDate.getMinutes())}`

    const options = Array.from(this.endTimeTarget.options).map(o => o.value)
    if (options.includes(exactTime)) {
      this.endTimeTarget.value = exactTime
    } else if (options.includes(snappedTime)) {
      this.endTimeTarget.value = snappedTime
    }
  }

  calculateTotalDuration() {
    let total = this.baseDurationValue
    this.addonCheckboxTargets.forEach(checkbox => {
      if (checkbox.checked) {
        total += parseInt(checkbox.dataset.duration, 10)
      }
    })
    return total
  }
}
